class AssemblerElement extends HTMLElement {
  constructor() {
    super();
    this.config = null;
    this.selectedMainProductId = null;
    this.selectedMainVariantId = null;
    this.selectedComplementaryProductId = null;
    this.selectedComplementaryVariantId = null;
    this.status = 'selecting_main_product';
    this.mainSwiper = null;
  }

  connectedCallback() {
    this.setup();
  }

  setup() {
    const configScript = this.querySelector('script[data-products-config]');
    if (configScript) {
      this.config = JSON.parse(configScript.textContent);
    }

    this.getAllElements();
    this.initSwiper();
    this.setEventListeners();
  }

  getAllElements() {
    this.mainProductImage = this.querySelector('.assemble-your-set__main-product-image');
    this.mainProductBadge = this.querySelector('.assemble-your-set__main-product-badge');
    this.mainProductForm = this.querySelector('.assemble-your-set__main-product-form');
    this.mainProductData = this.querySelector('.assemble-your-set__main-product-data');
    this.mainProductTitle = this.mainProductData?.querySelector('.assemble-your-set__product-title');
    this.mainProductPrice = this.mainProductData?.querySelector('.assemble-your-set__product-price');

    // Main product carousel elements
    this.mainSliderHolder = this.querySelector('.assemble-your-set__main-slider-holder');
    this.mainNavPrev = this.querySelector('[data-main-nav-prev]');
    this.mainNavNext = this.querySelector('[data-main-nav-next]');
    this.mainCurrentSlideEl = this.querySelector('[data-main-current-slide]');
    this.mainVariantsContainer = this.querySelector('[data-main-variants]');

    this.complementaryData = this.querySelector('.assemble-your-set__complementary-products-data');
    this.complementaryForm = this.querySelector('.assemble-your-set__complementary-products-form');
    this.complementaryTitle = this.querySelector('[data-comp-title]');
    this.complementaryPrice = this.querySelector('[data-comp-price]');
    this.complementaryVariantsContainer = this.querySelector('[data-comp-variants]');

    this.sliderHolder = this.querySelector('.assemble-your-set__slider-holder');
    this.navPrev = this.querySelector('[data-nav-prev]');
    this.navNext = this.querySelector('[data-nav-next]');
    this.currentSlideEl = this.querySelector('[data-current-slide]');
    this.sectionMessage = this.querySelector('.assemble-your-set__section-message');
    this.alreadyInCartMessage = this.querySelector('.assemble-your-set__section-already-in-cart-message');
    this.setInCartMessage = this.querySelector('.assemble-your-set__section-set-in-cart-message');

    this.submitButton = this.querySelector('[data-submit-assemble]');
    this.removeButton = this.querySelector('[data-remove-from-cart]');
    this.removeSetButton = this.querySelector('[data-remove-set-from-cart]');
  }

  initSwiper() {
    if (typeof Swiper === 'undefined') {
      console.warn('Swiper not loaded');
      return;
    }

    // Initialize main product carousel if enabled
    if (this.config.enableMainCarousel && this.mainSliderHolder) {
      this.mainSwiper = new Swiper(this.querySelector('.assemble-your-set__main-slider-container'), {
        slidesPerView: 1,
        spaceBetween: 0,
        centeredSlides: false,
        loop: false,
        on: {
          slideChange: () => {
            this.handleMainSlideChange();
          },
          click: (swiper, event) => {
            const slide = event.target.closest('.swiper-slide');
            if (slide) {
              this.handleMainSlideClick(slide);
            }
          }
        }
      });

      // Show first main product
      this.showInitialMainProduct();
    } else {
      // Single main product mode - select first product automatically
      this.selectedMainProductId = this.config.mainProducts[0]?.id;
    }

    // Initialize complementary products carousel
    this.swiper = new Swiper(this.querySelector('.assemble-your-set__slider-container'), {
      slidesPerView: 1,
      spaceBetween: 0,
      centeredSlides: false,
      loop: false,
      on: {
        slideChange: () => {
          this.handleSlideChange();
        },
        click: (swiper, event) => {
          const slide = event.target.closest('.swiper-slide');
          if (slide) {
            this.handleSlideClick(slide);
          }
        }
      }
    });

    // Show first product variants immediately
    this.showInitialComplementaryProduct();

    // Apply initial blocked state for complementary section
    this.updateComplementaryBlockState('selecting_main_product');
  }

  showInitialComplementaryProduct() {
    if (this.config.complementaryProducts.length > 0) {
      const firstProduct = this.config.complementaryProducts[0];
      this.selectedComplementaryProductId = firstProduct.id;
      this.renderComplementaryProduct(firstProduct);
      
      // Mark first slide as active
      const firstSlide = this.querySelector('.assemble-your-set__slider-container .swiper-slide');
      if (firstSlide) {
        firstSlide.classList.add('is-active');
      }

      // Update nav buttons initial state
      this.updateNavButtons();
    }
  }

  showInitialMainProduct() {
    if (this.config.mainProducts.length > 0) {
      const firstProduct = this.config.mainProducts[0];
      this.selectedMainProductId = firstProduct.id;
      this.renderMainProduct(firstProduct);
      
      // Mark first slide as active
      const firstSlide = this.querySelector('.assemble-your-set__main-slider-container .swiper-slide');
      if (firstSlide) {
        firstSlide.classList.add('is-active');
      }

      // Update nav buttons initial state
      this.updateMainNavButtons();
    }
  }

  handleMainSlideChange() {
    const activeIndex = this.mainSwiper.activeIndex;
    const product = this.config.mainProducts[activeIndex];
    
    if (product) {
      // Update counter
      if (this.mainCurrentSlideEl) {
        this.mainCurrentSlideEl.textContent = activeIndex + 1;
      }

      // Reset all badges and active states on all slides
      this.querySelectorAll('.assemble-your-set__main-slider-container .swiper-slide').forEach((s, i) => {
        s.classList.toggle('is-active', i === activeIndex);
        // Hide badge on all slides
        s.querySelector('.assemble-your-set__main-product-badge')?.classList.add('d-hidden');
      });

      // Reset variant selection
      this.selectedMainProductId = product.id;
      this.selectedMainVariantId = null;
      this.renderMainProduct(product);

      // Reset status back to selecting main product
      this.changeStatus('selecting_main_product');

      // Update nav button states
      this.updateMainNavButtons();
    }
  }

  handleMainSlideClick(slide) {
    const productId = parseInt(slide.dataset.mainProductId);
    const slideIndex = Array.from(this.querySelectorAll('.assemble-your-set__main-slider-container .swiper-slide')).indexOf(slide);
    
    // Navigate swiper to clicked slide
    if (this.mainSwiper && slideIndex !== this.mainSwiper.activeIndex) {
      this.mainSwiper.slideTo(slideIndex);
    }
  }

  renderMainProduct(product) {
    // Update title and price
    if (this.mainProductTitle) {
      this.mainProductTitle.textContent = product.title;
    }
    if (this.mainProductPrice) {
      this.mainProductPrice.textContent = this.formatPrice(product.price);
    }

    // Render size variants
    const variantsHtml = this.getUniqueSizes(product.variants).map(variant => `
      <input
        data-variant-id="${variant.id}"
        id="main-size-${product.handle}-${variant.size}-input"
        type="radio"
        name="main-size"
        value="${variant.size}"
        ${!variant.available ? 'disabled' : ''}
      >
      <label
        data-value="${variant.size}"
        for="main-size-${product.handle}-${variant.size}-input"
        title="${variant.size}"
        class="assemble-your-set__option-label product-option__label product-option__label--rounded product-option__label--circle"
        ${!variant.available ? 'disabled' : ''}
      >
        <span>${variant.size}</span>
        ${!variant.available ? '<span class="visually-hidden label-unavailable">Variante esgotada ou indisponível</span>' : ''}
      </label>
    `).join('');

    // Update variants in form
    const variantsContainer = this.mainProductForm?.querySelector('.assemble-your-set__product-variants');
    if (variantsContainer) {
      variantsContainer.innerHTML = variantsHtml;
    }
  }

  updateMainNavButtons() {
    if (!this.mainSwiper) return;
    
    if (this.mainNavPrev) {
      this.mainNavPrev.disabled = this.mainSwiper.isBeginning;
    }
    if (this.mainNavNext) {
      this.mainNavNext.disabled = this.mainSwiper.isEnd;
    }
  }

  handleSlideChange() {
    const activeIndex = this.swiper.activeIndex;
    const product = this.config.complementaryProducts[activeIndex];
    
    if (product) {
      // Update counter
      if (this.currentSlideEl) {
        this.currentSlideEl.textContent = activeIndex + 1;
      }

      // Reset all badges and active states on all complementary slides
      this.querySelectorAll('.assemble-your-set__slider-container .swiper-slide').forEach((s, i) => {
        s.classList.toggle('is-active', i === activeIndex);
        // Hide badge on all slides
        s.querySelector('.assemble-your-set__slide-badge')?.classList.add('d-hidden');
      });

      // Reset variant selection
      this.selectedComplementaryProductId = product.id;
      this.selectedComplementaryVariantId = null;
      this.renderComplementaryProduct(product);
      
      // Update status if main product was already selected
      if (this.selectedMainVariantId) {
        this.changeStatus('selecting_complementary_product');
      }

      // Update nav button states
      this.updateNavButtons();
    }
  }

  updateNavButtons() {
    if (!this.swiper) return;
    
    if (this.navPrev) {
      this.navPrev.disabled = this.swiper.isBeginning;
    }
    if (this.navNext) {
      this.navNext.disabled = this.swiper.isEnd;
    }
  }

  setEventListeners() {
    // Main product size selection
    this.mainProductForm?.addEventListener('change', (e) => {
      if (e.target.type === 'radio') {
        this.handleMainSizeChange(e.target);
      }
    });

    // Complementary product size selection
    this.complementaryForm?.addEventListener('change', (e) => {
      if (e.target.type === 'radio') {
        this.handleComplementarySizeChange(e.target);
      }
    });

    // Submit button
    this.submitButton?.addEventListener('click', () => this.handleSubmit());

    // Remove buttons
    this.removeButton?.addEventListener('click', () => this.handleRemove());
    this.removeSetButton?.addEventListener('click', () => this.handleRemoveSet());

    // Complementary products navigation buttons
    this.navPrev?.addEventListener('click', () => this.swiper?.slidePrev());
    this.navNext?.addEventListener('click', () => this.swiper?.slideNext());

    // Main products navigation buttons (carousel mode)
    this.mainNavPrev?.addEventListener('click', () => this.mainSwiper?.slidePrev());
    this.mainNavNext?.addEventListener('click', () => this.mainSwiper?.slideNext());
  }

  handleMainSizeChange(input) {
    const variantId = parseInt(input.dataset.variantId);
    this.selectedMainVariantId = variantId;

    // Find the current main product (from carousel or single)
    const currentMainProduct = this.config.mainProducts.find(p => p.id === this.selectedMainProductId) || this.config.mainProducts[0];
    
    // Update image if variant has specific image
    const variant = currentMainProduct?.variants.find(v => v.id === variantId);

    // Update price to match selected variant
    if (variant && this.mainProductPrice) {
      this.mainProductPrice.textContent = this.formatPrice(variant.price);
    }

    if (this.config.enableMainCarousel) {
      // Carousel mode: update only the active slide's image
      const activeSlide = this.querySelector('.assemble-your-set__main-slider-container .swiper-slide.is-active');
      if (activeSlide) {
        if (variant?.image) {
          const slideImage = activeSlide.querySelector('.assemble-your-set__main-product-image');
          if (slideImage) {
            slideImage.src = variant.image;
          }
        }
        // Show badge on active slide
        activeSlide.querySelector('.assemble-your-set__main-product-badge')?.classList.remove('d-hidden');
      }
    } else {
      // Single product mode: update the main product image
      if (variant?.image && this.mainProductImage) {
        this.mainProductImage.src = variant.image;
      }
    }

    this.changeStatus('main_product_selected');
  }

  handleSlideClick(slide) {
    const productId = parseInt(slide.dataset.productId);
    const slideIndex = Array.from(this.querySelectorAll('.swiper-slide')).indexOf(slide);
    
    // Navigate swiper to clicked slide
    if (this.swiper && slideIndex !== this.swiper.activeIndex) {
      this.swiper.slideTo(slideIndex);
      return; // handleSlideChange will handle the rest
    }

    this.selectedComplementaryProductId = productId;
    this.selectedComplementaryVariantId = null;

    // Remove active class from all slides
    this.querySelectorAll('.swiper-slide').forEach(s => {
      s.classList.remove('is-active');
      s.querySelector('.assemble-your-set__slide-badge')?.classList.add('d-hidden');
    });

    // Add active class to selected slide
    slide.classList.add('is-active');

    // Find product and render variants
    const product = this.config.complementaryProducts.find(p => p.id === productId);
    if (product) {
      this.renderComplementaryProduct(product);
      if (this.selectedMainVariantId) {
        this.changeStatus('selecting_complementary_product');
      }
    }
  }

  renderComplementaryProduct(product) {
    // Update title and price
    if (this.complementaryTitle) {
      this.complementaryTitle.textContent = product.title;
    }
    if (this.complementaryPrice) {
      this.complementaryPrice.textContent = this.formatPrice(product.price);
    }

    // Render size variants with unique IDs to avoid conflicts
    const productHandle = product.handle || product.id;
    const variantsHtml = this.getUniqueSizes(product.variants).map(variant => `
      <input
        data-variant-id="${variant.id}"
        id="comp-size-${productHandle}-${variant.size}-input"
        type="radio"
        name="complementary-size"
        value="${variant.size}"
        ${!variant.available ? 'disabled' : ''}
      >
      <label
        data-value="${variant.size}"
        for="comp-size-${productHandle}-${variant.size}-input"
        title="${variant.size}"
        class="assemble-your-set__option-label product-option__label product-option__label--rounded product-option__label--circle"
        ${!variant.available ? 'disabled' : ''}
      >
        <span>${variant.size}</span>
        ${!variant.available ? '<span class="visually-hidden label-unavailable">Variante esgotada ou indisponível</span>' : ''}
      </label>
    `).join('');

    if (this.complementaryVariantsContainer) {
      this.complementaryVariantsContainer.innerHTML = variantsHtml;
    }

    // Show complementary form
    this.complementaryData?.classList.remove('d-hidden');
    this.complementaryForm?.classList.remove('d-hidden');
  }

  getUniqueSizes(variants) {
    const seen = new Set();
    return variants.filter(v => {
      if (seen.has(v.size)) return false;
      seen.add(v.size);
      return true;
    });
  }

  handleComplementarySizeChange(input) {
    const variantId = parseInt(input.dataset.variantId);
    this.selectedComplementaryVariantId = variantId;

    // Update price to match selected variant
    const currentCompProduct = this.config.complementaryProducts.find(p => p.id === this.selectedComplementaryProductId);
    const variant = currentCompProduct?.variants.find(v => v.id === variantId);
    if (variant && this.complementaryPrice) {
      this.complementaryPrice.textContent = this.formatPrice(variant.price);
    }

    // Show badge on selected complementary slide (use specific selector)
    const activeSlide = this.querySelector('.assemble-your-set__slider-container .swiper-slide.is-active');
    if (activeSlide) {
      activeSlide.querySelector('.assemble-your-set__slide-badge')?.classList.remove('d-hidden');
    }

    this.changeStatus('complementary_product_selected');
  }

  changeStatus(newStatus) {
    this.status = newStatus;
    this.dataset.status = newStatus;

    // Update message
    const message = this.config.statusDictionary.section_message[newStatus];
    if (this.sectionMessage && message) {
      const stepNumber = this.getStepNumber(newStatus);
      this.sectionMessage.innerHTML = `<strong>Passo ${stepNumber}: </strong>${message}`;
    }

    // Update button text
    const buttonText = this.config.statusDictionary.button_text[newStatus];
    if (this.submitButton && buttonText) {
      this.submitButton.textContent = buttonText;
    }

    // Show/hide main product badge
    if (newStatus !== 'selecting_main_product') {
      this.mainProductBadge?.classList.remove('d-hidden');
    } else {
      this.mainProductBadge?.classList.add('d-hidden');
    }

    // Block/unblock complementary section based on main product selection (carousel mode)
    this.updateComplementaryBlockState(newStatus);

    // Slider is always enabled now
    this.sliderHolder?.classList.remove('is-disabled');

    // Disable button based on status
    this.updateButtonState();
  }

  updateComplementaryBlockState(status) {
    const isBlocked = status === 'selecting_main_product';
    
    // Toggle blocked class on complementary elements (but NOT the slider holder - keep carousel always free)
    this.complementaryData?.classList.toggle('is-blocked', isBlocked);
    this.complementaryForm?.classList.toggle('is-blocked', isBlocked);

    // Disable/enable complementary variant inputs
    const complementaryInputs = this.complementaryVariantsContainer?.querySelectorAll('input[type="radio"]');
    complementaryInputs?.forEach(input => {
      input.disabled = isBlocked || input.hasAttribute('data-unavailable');
    });
  }

  getStepNumber(status) {
    switch (status) {
      case 'selecting_main_product':
        return 1;
      case 'main_product_selected':
      case 'selecting_complementary_product':
        return 2;
      case 'complementary_product_selected':
      case 'finished':
        return 3;
      default:
        return 1;
    }
  }

  updateButtonState() {
    if (!this.submitButton) return;

    // Button is only enabled when both products have selected variants
    const bothSelected = this.selectedMainVariantId && this.selectedComplementaryVariantId;
    this.submitButton.disabled = !bothSelected || this.status === 'finished';
  }

  async handleSubmit() {
    // Only proceed if both variants are selected
    if (!this.selectedMainVariantId || !this.selectedComplementaryVariantId) {
      return;
    }

    // Disable button during submission
    this.submitButton.disabled = true;
    
    // Add both products at once
    const result = await this.addBothToCart();
    if (result?.error) {
      if (this.sectionMessage) {
        this.sectionMessage.textContent = result.message;
        this.sectionMessage.setAttribute('role', 'alert');
      }
      this.submitButton.disabled = false;
      return;
    }
    this.sectionMessage?.removeAttribute('role');
    this.changeStatus('finished');
    await this.refreshCartAndOpen();
  }

  async addBothToCart() {
    try {
      const items = [
        { id: this.selectedMainVariantId, quantity: 1, properties: { '_origem': 'Monte seu Conjunto - PDP Crossell' } },
        { id: this.selectedComplementaryVariantId, quantity: 1, properties: { '_origem': 'Monte seu Conjunto - PDP Crossell' } }
      ];

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });

      const data = await response.json();
      if (!response.ok) {
        return { error: true, message: data.description || data.message || 'Não foi possível adicionar o conjunto. Tente novamente.' };
      }
      return data;
    } catch (error) {
      console.error('Error adding to cart:', error);
      return { error: true, message: 'Erro de conexão ao adicionar o conjunto. Confira o carrinho antes de tentar novamente.' };
    }
  }

  async addToCart(variantId) {
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1, properties: { '_origem': 'Monte seu Conjunto - PDP Crossell' } }]
        })
      });

      if (!response.ok) throw new Error('Failed to add to cart');

      // Trigger cart update event
      if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'assemble-your-set' });
      }

      return await response.json();
    } catch (error) {
      console.error('Error adding to cart:', error);
    }
  }

  async handleRemove() {
    if (this.selectedMainVariantId) {
      await this.removeFromCart(this.selectedMainVariantId);
      this.resetToStart();
    }
  }

  async handleRemoveSet() {
    const promises = [];
    if (this.selectedMainVariantId) {
      promises.push(this.removeFromCart(this.selectedMainVariantId));
    }
    if (this.selectedComplementaryVariantId) {
      promises.push(this.removeFromCart(this.selectedComplementaryVariantId));
    }
    await Promise.all(promises);
    this.resetToStart();
  }

  async removeFromCart(variantId) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 0 })
      });

      if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'assemble-your-set' });
      }

      return await response.json();
    } catch (error) {
      console.error('Error removing from cart:', error);
    }
  }

  resetToStart() {
    this.selectedMainVariantId = null;
    this.selectedComplementaryProductId = null;
    this.selectedComplementaryVariantId = null;

    // Reset forms
    this.mainProductForm?.reset();
    this.complementaryForm?.reset();

    // Hide complementary section
    this.complementaryData?.classList.add('d-hidden');
    this.complementaryForm?.classList.add('d-hidden');

    // Remove active states from slides
    this.querySelectorAll('.swiper-slide').forEach(s => {
      s.classList.remove('is-active');
      s.querySelector('.assemble-your-set__slide-badge')?.classList.add('d-hidden');
    });

    this.changeStatus('selecting_main_product');
  }

  async refreshCartAndOpen() {
    try {
      // Fetch updated cart to trigger Shopify's cart update
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      // Dispatch cart update event for theme integration
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      
      // Try Dawn's cart drawer
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        // Dawn uses a fetch to update cart contents
        const drawerResponse = await fetch('/?section_id=cart-drawer');
        const html = await drawerResponse.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newDrawer = doc.querySelector('cart-drawer');
        
        if (newDrawer) {
          cartDrawer.innerHTML = newDrawer.innerHTML;
        }
        
        // Open the drawer
        if (typeof cartDrawer.open === 'function') {
          cartDrawer.open();
        } else {
          cartDrawer.classList.add('is-open', 'active');
          document.body.classList.add('overflow-hidden');
        }
      }

      // Fallback: update cart icon count
      const cartCount = document.querySelector('.cart-count-bubble span');
      if (cartCount && cart.item_count) {
        cartCount.textContent = cart.item_count;
      }
    } catch (e) {
      console.warn('Cart update error:', e);
      // Fallback: redirect to cart page
      window.location.href = '/cart';
    }
  }

  formatPrice(cents) {
    const price = (cents / 100).toFixed(2).replace('.', ',');
    return `R$ ${price}`;
  }
}

if (!customElements.get('assembler-element')) {
  customElements.define('assembler-element', AssemblerElement);
}
